import { Injectable, OnDestroy } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { ConfigurationService } from '../../config/configuration.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { AuthRedirectDebugService } from '../auth-redirect-debug.service';
import { AUTH_REDIRECT_STATE_KEY, AuthRedirectReason } from '../auth-redirect-reason';
import { AuthEventType } from '../models/auth-events.model';
import { AuthIdleService } from './auth-idle.service';
import { AuthSessionService } from './auth-session.service';
import { AuthTokenService } from './auth-token.service';
import { KeycloakClientService } from './keycloak-client.service';

@UntilDestroy()
@Injectable({
    providedIn: 'root',
})
export class AuthLifecycleService implements OnDestroy {
    private idleWatcherStarted = false;
    private tokenRefreshIntervalId?: ReturnType<typeof setInterval>;

    constructor(
        private keycloakClient: KeycloakClientService,
        private authSession: AuthSessionService,
        private authToken: AuthTokenService,
        private authIdle: AuthIdleService,
        private configService: ConfigurationService,
        private notificationService: NotificationsService,
        private authRedirectDebug: AuthRedirectDebugService,
        private router: Router
    ) {}

    initialize(): void {
        this.router.events
            .pipe(
                filter((event): event is NavigationStart => event instanceof NavigationStart),
                untilDestroyed(this)
            )
            .subscribe(() => this.refreshTokenIfLoggedIn());

        this.authSession.authEvents$.pipe(untilDestroyed(this)).subscribe(event => {
            if (event.type === AuthEventType.OnAuthLogout) {
                this.stopTokenRefreshSchedule();
                this.idleWatcherStarted = false;
                this.authIdle.stopTimer();
                this.notificationService.destroy();
            } else if (event.type === AuthEventType.OnAuthRefreshError) {
                if (this.keycloakClient.isInitialized()) {
                    this.stopTokenRefreshSchedule();
                    this.idleWatcherStarted = false;
                    this.authIdle.stopTimer();
                    this.notificationService.destroy();
                }
            } else if (event.type === AuthEventType.OnAuthSuccess) {
                this.startIfAuthenticated();
            }
        });

        this.startIfAuthenticated();
    }

    onVisibilityChange(): void {
        if (document.visibilityState === 'visible') {
            this.refreshTokenIfLoggedIn();
        }
    }

    startIfAuthenticated(): void {
        if (!this.keycloakClient.isLoggedIn()) {
            return;
        }

        this.startIdleWatcher();
        this.startTokenRefreshSchedule();
        this.notificationService.init();
    }

    ngOnDestroy(): void {
        this.stopTokenRefreshSchedule();
    }

    private startIdleWatcher(): void {
        if (!this.keycloakClient.isLoggedIn()) {
            return;
        }

        if (this.idleWatcherStarted) {
            this.authIdle.resetTimer();
            return;
        }

        this.idleWatcherStarted = true;
        const sessionIdleSeconds = this.configService.getConfig().auth.sessionIdleSeconds;

        this.authIdle
            .startWatching(sessionIdleSeconds)
            .pipe(untilDestroyed(this))
            .subscribe(isTimeout => {
                if (isTimeout && this.keycloakClient.isLoggedIn()) {
                    this.authRedirectDebug.log(AuthRedirectReason.IdleTimeout, {
                        sessionIdleSeconds,
                    });
                    void this.router.navigateByUrl('/logout', {
                        state: { [AUTH_REDIRECT_STATE_KEY]: AuthRedirectReason.IdleTimeout },
                    });
                }
            });
    }

    private startTokenRefreshSchedule(): void {
        this.stopTokenRefreshSchedule();

        if (!this.keycloakClient.isLoggedIn()) {
            return;
        }

        this.tokenRefreshIntervalId = setInterval(() => {
            if (document.visibilityState === 'visible') {
                this.refreshTokenIfLoggedIn();
            } else {
                this.refreshTokenIfNearExpiryWhenLoggedIn();
            }
        }, this.getTokenRefreshIntervalMs());
    }

    private stopTokenRefreshSchedule(): void {
        if (this.tokenRefreshIntervalId !== undefined) {
            clearInterval(this.tokenRefreshIntervalId);
            this.tokenRefreshIntervalId = undefined;
        }
    }

    private refreshTokenIfLoggedIn(): void {
        if (!this.keycloakClient.isInitialized() || !this.keycloakClient.isLoggedIn()) {
            return;
        }

        void this.authToken.ensureTokenFresh();
    }

    private refreshTokenIfNearExpiryWhenLoggedIn(): void {
        if (!this.keycloakClient.isInitialized() || !this.keycloakClient.isLoggedIn()) {
            return;
        }

        void this.authToken.ensureTokenFreshIfNearExpiry();
    }

    private getTokenRefreshIntervalMs(): number {
        return this.configService.getConfig().auth.tokenRefreshIntervalSeconds * 1000;
    }
}
